import { BreakPoint } from '@adornis/base/client/frontend-helpers/window-size.js';
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BHPromptWorkshopAppointmentsView } from '@adornis/bh-registration/client/bh-prompt-appointments-view.js';
import { getColor } from '@adornis/bh-registration/client/helper/getColor.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-components/authentication/bh-input-auth-code.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { getWorkshopRecommendations } from '@adornis/buchungsheld-db/api/recommendations.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { RegistrationData } from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { formatPrice, productPriceNumber } from '@adornis/buchungsheld-db/global-helpers.js';
import { german_iso_3166 } from '@adornis/buchungsheld-db/plugins/institution-statistics/icsrm-helper.js';
import { translations } from '@adornis/buchungsheld-db/translations/translation.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/components/tr-button-small';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import type { Language } from '@adornis/translation-core/languages.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, filter, switchMap, type Observable } from 'rxjs';

@customElement('bh-page-booked')
export class BHPageRegistrationBooked extends BuchungsheldLitElement {
  @state() hideButton = false;
  @state() user = new RXController(this, currentUser() as Observable<User>);
  @state() _institution = new RXController(
    this,
    // ToDo: Mit ArrowSelection genauer statt selectionSet
    /*Institution.getByID<Institution>(this.institutionID)({
      ...Institution.allFields,
      settings: { ...Setting.allFields, colorDefinitions: ColorDefinitions.allFields },
    }),*/
    getByID(
      Institution,
      this.institutionID,
    )({
      _id: 1,
      settings: {
        inheritedColorDefinitions: {
          value: {
            registrationColorDefinitions: {
              productColor: 1,
              productFontColor: 1,
              moreProductsFontColor: 1,
              moreProductsColor: 1,
              newPriceColor: 1,
              oldPriceColor: 1,
              stepColor: 1,
            },
          },
        },
        colorDefinitions: {
          registrationColorDefinitions: {
            productColor: 1,
            productFontColor: 1,
            moreProductsFontColor: 1,
            moreProductsColor: 1,
            newPriceColor: 1,
            oldPriceColor: 1,
            stepColor: 1,
          },
        },
      },
    }),
  );

  @state() registrationData!: RegistrationData;

  private readonly _productIDs$ = new BehaviorSubject<string[]>([]);
  @state() products = new RXController(
    this,
    this._productIDs$.pipe(
      switchMap(ids =>
        Promise.all(ids.map(id => getByID(Product, id)({ name: 1, appointments: { start: 1, end: 1 } }))),
      ),
    ),
  );

  @state() furtherInteresstingProducts = new RXController(
    this,
    this.user.observable.pipe(
      filter(isDefined),
      switchMap(user =>
        getWorkshopRecommendations(
          this.institutionID,
          user._id,
          5,
        )({
          _id: 1,
          name: 1,
          institutionID: 1,
          translatedDescriptionHTML: TextMultipleLangItem.allFields,
          timeframe: Timeframe.allFields,
          timeframeID: 1,
          presenceType: 1,
          language: 1,
          capacity: 1,
          hideCapacity: 1,
          leftCapacity: 1,
          topic: 1,
          category: Category.allFields,
          isLDAPOnly: 1,
          hasPrice: 1,
          prices: { unitAmount: 1 },
          appointments: { start: 1, end: 1 },
        }),
      ),
    ),
  );

  @state() private readonly _language = new RXController(this, globalLanguageSubject, obs => obs);
  @state() private readonly _componentSize = new ComponentSizeController(this, { debounceTime: 100 });

  @state() _curStammDaten = new RXController(
    this,
    combineLatest([this.user.observable, this._institution.observable, this.products.observable]).pipe(
      switchMap(async ([curUser, curInstitution, curProds]) => {
        let targetData;

        if (!!curUser && !!curInstitution)
          targetData = await UserStammdaten.getByUserAndInstitution(
            curUser._id,
            curInstitution._id,
          )(selectionSet(() => UserStammdaten, 5));

        return targetData;
      }),
    ),
  );

  @state() shareOptions = new RXController(this, {
    content: this._translation.translate('share', 'content'),
    url: window.location.href,
    hashtags: ['#buchungsheld'],
  });

  private get institutionID() {
    const id = routingState.getValue().searchParams.get('institution');
    if (!id) throw new Error('institutioID required');
    return id;
  }

  private get _isMobileComponent() {
    return this._componentSize.componentDevice <= BreakPoint.bigPhone;
  }

  private get _coreDataEntries() {
    return Object.entries(
      (this._curStammDaten.value?.constructor as typeof UserStammdaten | undefined)?.fields ?? {},
    ).filter(([key]) => !['_class', '_id', 'userID', 'institutionID'].includes(key));
  }

  override connectedCallback() {
    super.connectedCallback();

    const data = routingState.getValue().searchParams.get('data') ?? '{}';
    this.registrationData = new RegistrationData(JSON.parse(data));
    this._productIDs$.next(this.registrationData.productData.map(pd => pd.productID as string));
  }

  private openWorkshopDetailsDialog(workshop: Workshop) {
    const institution = this._institution.value;

    return BHPromptWorkshopAppointmentsView.showPopup({
      props: {
        hasCloseButton: true,
        workshop,
        productColor: getColor(
          institution?.settings.colorDefinitions?.registrationColorDefinitions?.productColor,
          institution?.settings.inheritedColorDefinitions?.value?.registrationColorDefinitions.productColor,
          this.colors.tone.black,
        ),
        productFontColor: getColor(
          institution?.settings.colorDefinitions?.registrationColorDefinitions?.productFontColor,
          institution?.settings.inheritedColorDefinitions?.value?.registrationColorDefinitions.productFontColor,
          '#fff',
        ),
      },
    });
  }

  override render() {
    if (this.products.isLoading || this._institution.isLoading) return html` <x-loading-state></x-loading-state> `;

    const card = (content: TemplateResult) => {
      const inner = html` <tr-flex space="xl"> ${content} </tr-flex> `;
      if (this.clientWidth < 500) return inner;
      return html` <x-card ${css({ width: '100%' })}> ${inner} </x-card> `;
    };

    const curUser = this.user.value;

    if (this._curStammDaten.isLoading)
      return html`
        <tr-flex padding="xl" w100 center crossaxis-center>
          <tr-card padding="lg">
            <tr-loader></tr-loader>
          </tr-card>
        </tr-flex>
      `;

    const userStammDaten = this._curStammDaten.value;

    if (!userStammDaten)
      return html`
        <tr-flex padding="xl" w100 center crossaxis-center>
          <tr-card padding="lg">
            <tr-h3> Keine Stammdaten gefunden! </tr-h3>
          </tr-card>
        </tr-flex>
      `;

    const tn = userStammDaten?.getTranslationNamespace();

    const main = html`
      <x-flex space="lg">
        <tr-text> ${this._translation.translate('REGISTRATION', 'overview_send')} </tr-text>

        <!-- booked workshops -->
        <x-flex space="md">
          <x-flex horizontal space="sm" crossaxis-center>
            <tr-h3>${this._translation.translate('REGISTRATION', 'events_booked')}</tr-h3>
            <x-hr flex></x-hr>
          </x-flex>
          <tr-grid
            sub-content
            columns="${this.spacing.md}px 1fr ${this.spacing.md}px"
            space="xs"
            ${acss({
              alignItems: 'center',

              '[event-item]': {
                display: 'contents',

                'tr-icon': {
                  '&[event-icon]': {
                    color: this.colors.dark.shade(300),
                    paddingLeft: '3px',
                  },

                  '&[open-btn]': {
                    color: this.colors.accent,
                    fontSize: '1.25em',
                    paddingLeft: '3px',
                  },
                },
                'tr-text': {
                  padding: '0',
                },
              },
            })}
          >
            ${this.products.value?.map(product => {
              if (!product) return nothing;
              const isWorkshop = product._class === Workshop._class;

              return html`
                <tr-flex
                  data-test
                  event-item
                  @click=${() => isWorkshop && this.openWorkshopDetailsDialog(product as Workshop)}
                  ${css({
                    cursor: isWorkshop ? 'pointer' : 'default',
                  })}
                >
                  <tr-icon> event </tr-icon>
                  <tr-text> ${product?.name} </tr-text>
                  ${isWorkshop
                    ? html`<tr-button open-btn mode="icon" icon="manage_search"> </tr-button> `
                    : html` <div></div> `}
                </tr-flex>
              `;
            })}
          </tr-grid>
        </x-flex>

        <!-- personal data -->
        <x-flex space="md">
          <x-flex horizontal space="xs" crossaxis-center>
            <tr-h3>${this._translation.translate('REGISTRATION', 'personal_data')}</tr-h3>
            <x-hr flex></x-hr>
          </x-flex>

          <tr-grid sub-content columns="max-content 1fr">
            <tr-text first-row-col> ${this._translation.translate('field', 'email')} </tr-text>
            <tr-text>${curUser?.email}</tr-text>
            <tr-text first-row-col> ${this._translation.translate('field', 'firstName')} </tr-text>
            <tr-text>${curUser?.firstName}</tr-text>
            <tr-text first-row-col> ${this._translation.translate('field', 'lastName')} </tr-text>
            <tr-text>${curUser?.lastName}</tr-text>
          </tr-grid>
        </x-flex>

        ${this._coreDataEntries.length
          ? html`
              <x-flex space="md">
                <x-flex horizontal space="sm" crossaxis-center>
                  <tr-h3>${this._translation.translate('REGISTRATION', 'stammdaten')}</tr-h3>
                  <x-hr flex></x-hr>
                </x-flex>
                <tr-grid sub-content columns="minmax(min-content, 100px) 1fr">
                  ${repeat(this._coreDataEntries, ([key, fieldDef]) => {
                    const trKeys = tn && translations?.[tn]?.field?.[key] ? [tn, 'field', key] : ['field', key];
                    const isEnum = !!fieldDef.type()?._isBaseQLEnum;
                    const isBoolean = fieldDef.type()?.name === 'Boolean';
                    const maybeValue = userStammDaten[key];
                    const value = (() => {
                      if (isBoolean)
                        return maybeValue ? this._translation.translate('yes') : this._translation.translate('no');
                      if (!maybeValue) return '';
                      if (isEnum) return this._translation.translate('enums', maybeValue);
                      if (maybeValue instanceof DateTime) return maybeValue.toFormat('dd.LL.yyyy');
                      if (german_iso_3166[maybeValue]) return german_iso_3166[maybeValue];
                      return maybeValue;
                    })();
                    console.log({
                      key,
                      fieldDef,
                      trKeys,
                      isEnum,
                      isBoolean,
                      maybeValue,
                      value,
                      type: fieldDef.type(),
                    });
                    return !value
                      ? nothing
                      : html`
                          <tr-text first-row-col> ${this._translation.translate(...trKeys)} </tr-text>
                          <tr-text> ${value ?? '-'} </tr-text>
                        `;
                  })}
                </tr-grid>
              </x-flex>
            `
          : nothing}
        ${this.hideButton
          ? nothing
          : html` <x-button @click=${() => goTo('/profile')}> ${this._translation.translate('finish')} </x-button> `}
        <!-- <bh-share flex only-icons .shareOptions=${this.shareOptions.value}></bh-share> -->
      </x-flex>
    `;

    const productRecommendations = (() => {
      const productRecommendations = this.furtherInteresstingProducts.value;
      if (!productRecommendations || productRecommendations.length === 0) return nothing;

      return html`
        <x-flex
          ${css({ padding: this.clientWidth > 500 ? `${this.spacing.lg} 2rem` : `${this.spacing.lg} 0rem` })}
          vertical
          space="md"
        >
          <x-h2>${this._translation.translate('workshop', 'workshopRecommendations')}</x-h2>
          ${productRecommendations.map(product => this.renderProductCard(product))}
        </x-flex>
      `;
    })();

    return html`
      <tr-section>
        <x-flex h100 ${css({ padding: `${this.spacing.lg} 0` })}> ${card(main)} </x-flex>

        ${productRecommendations}
      </tr-section>
    `;
  }

  private renderProductCard(
    workshop: NarrowedEntity<
      Workshop,
      {
        _id: 1;
        name: 1;
        start: 1;
        end: 1;
        hideCapacity: 1;
        hasPrice: 1;
        leftCapacity: 1;
        isLDAPOnly: 1;
        presenceType: 1;
        language: 1;
        topic: 1;
        prices: { unitAmount: 1 };
        timeframe: { name: 1 };
        translatedDescriptionHTML: { langKey: 1; text: 1 };
        appointments: { start: 1; end: 1 };
      }
    >,
  ) {
    const colorDefinitions = this._institution.value?.settings.colorDefinitions;
    const inheritedColorDefinitions = this._institution.value?.settings.inheritedColorDefinitions;

    return html`
      <tr-basic-card
        ${css({
          width: '100%',
          background: 'white',
          position: 'relative',
          overflow: 'hidden',
          padding: '20px',
          cursor: 'pointer',
          userSelect: 'none',
        })}
        @click=${() => {
          const url = A.absoluteUrl(
            '/registration?step=personal&institution=' +
              this.institutionID +
              '&data=%7B%22_class%22%3A%22RegistrationData%22%2C%22institutionID%22%3A%22testing_institution_2%22%2C%22productData%22%3A%5B%7B%22_class%22%3A%22RegistrationWorkshopData%22%2C%22_id%22%3A%2266fd143d20e1ae0ba7020%22%2C%22productID%22%3A%22' +
              workshop._id +
              '%22%7D%5D%2C%22acceptedTerms%22%3Afalse%2C%22userStammdaten%22%3Anull%2C%22user%22%3Anull%7D',
          );
          goTo(url);
        }}
      >
        <!-- time name description start -->

        <tr-flex ?horizontal=${!this._isMobileComponent} space="md" crossaxis-start>
          ${(() => {
            if (!workshop.appointments || workshop.appointments.length === 0) return nothing;
            return html`
              <tr-flex space="sm">
                ${this._timeSlotTime(workshop.start, workshop.end)}
                <tr-button
                  mode="outline"
                  ${css({
                    borderColor: getColor(
                      colorDefinitions?.registrationColorDefinitions?.moreProductsFontColor,
                      inheritedColorDefinitions?.value?.registrationColorDefinitions?.moreProductsFontColor,
                      this.colors.tone.black,
                    ),
                    color: getColor(
                      colorDefinitions?.registrationColorDefinitions?.moreProductsFontColor,
                      inheritedColorDefinitions?.value?.registrationColorDefinitions.moreProductsFontColor,
                      this.colors.tone.black,
                    ),
                    background: getColor(
                      colorDefinitions?.registrationColorDefinitions?.moreProductsColor,
                      inheritedColorDefinitions?.value?.registrationColorDefinitions.moreProductsColor,
                      'white',
                    ),
                    zIndex: '10',
                  })}
                  @click=${event => {
                    event.stopPropagation(); // necessary because parent also has click listener which should not get triggered
                    return BHPromptWorkshopAppointmentsView.showPopup({
                      props: {
                        hasCloseButton: true,
                        workshop,
                        productColor: getColor(
                          colorDefinitions?.registrationColorDefinitions?.productColor,
                          inheritedColorDefinitions?.value?.registrationColorDefinitions.productColor,
                          this.colors.tone.black,
                        ),
                        productFontColor: getColor(
                          colorDefinitions?.registrationColorDefinitions?.productFontColor,
                          inheritedColorDefinitions?.value?.registrationColorDefinitions.productFontColor,
                          '#fff',
                        ),
                      },
                    });
                  }}
                >
                  ${(() => {
                    for (const appointment of workshop.appointments) {
                      if (appointment.start === null)
                        return this._translation.translate('termin', 'dateNotYetDetermined');
                    }

                    return workshop.appointments.length === 1 && workshop.appointments[0]
                      ? `${workshop.appointments[0]!.start?.toLocaleString(
                          DateTime.TIME_24_SIMPLE,
                        )} - ${workshop.appointments[0]!.end?.toLocaleString(DateTime.TIME_24_SIMPLE)} Uhr`
                      : this._translation.translate('termin', 'all_termins');
                  })()}
                </tr-button>
              </tr-flex>
            `;
          })()}

          <tr-flex space="lg" w100 flex ${css({ minWidth: '0' })}>
            <tr-flex space="sm">
              <tr-h3 ${css({ paddingRight: this._isMobileComponent ? 'unset' : '120px' })}>${workshop.name}</tr-h3>

              <bh-expandable-content>
                <tr-flex ${css({ gap: '1em' })}>
                  ${unsafeHTML(
                    this.chooseValueByLang(
                      TextMultipleLangItem.mapAllTranslationsByLang(workshop.translatedDescriptionHTML),
                    ),
                  )}
                </tr-flex>
              </bh-expandable-content>
            </tr-flex>

            <!-- select more-products price start -->
            <tr-flex ${css({ flexDirection: this.clientWidth < 700 ? 'column' : 'row' })} space="md" crossaxis-start>
              <tr-flex flex space="xxs" horizontal wrap ${css({ order: this.clientWidth < 700 ? '99' : 'unset' })}>
                ${this.renderChipsForProduct(workshop)}
                ${!workshop.hideCapacity
                  ? html`
                      <tr-chip> ${workshop.leftCapacity} ${this._translation.translate('WORKSHOP_CAPACITY')} </tr-chip>
                    `
                  : nothing}
                ${workshop.leftCapacity <= 0
                  ? html`
                      <tr-chip ${css({ background: this.colors.tone.warning, color: this.colors.tone.black })}>
                        ${this._translation.translate('REGISTRATION', 'queue_possible')}
                      </tr-chip>
                    `
                  : nothing}
              </tr-flex>
              ${workshop.hasPrice ? this._renderPrice(productPriceNumber(workshop)) : nothing}
            </tr-flex>
            <!-- select more-products price start -->
          </tr-flex>
        </tr-flex>
        <!-- time name description end -->
      </tr-basic-card>
    `;
  }

  private renderChipsForProduct(
    product: NarrowedEntity<
      Product,
      {
        isLDAPOnly: 1;
        timeframe: { name: 1 };
        translatedDescriptionHTML: { langKey: 1; text: 1 };
        presenceType: 1;
        language: 1;
        topic: 1;
      }
    >,
  ) {
    return html`
      ${product.isLDAPOnly
        ? html` <tr-chip>${this._translation.translate('PROFILE', 'LDAP_ACCOUNT_REQUIRED')}</tr-chip> `
        : nothing}
      ${product.timeframe ? html` <tr-chip>${product.timeframe.name}</tr-chip> ` : nothing}

      <!-- presence type -->
      ${(() => {
        if (!product.presenceType || product.presenceType === 'not_selected') return nothing;
        const text = this._translation.translate('PRODUCT', 'presenceTypes', product.presenceType);
        return html` <tr-chip> ${text} </tr-chip> `;
      })()}

      <!-- TODO: activate me again -->
      ${product.language && false
        ? html`
            <tr-chip
              >${this._translation.translate('PRODUCT', 'language')}:
              ${this._translation.translate('PRODUCT', 'languages', product.language)}</tr-chip
            >
          `
        : nothing}
      ${product.topic ? html` <tr-chip>#${product.topic}</tr-chip> ` : nothing}
    `;
  }

  private _renderPrice(
    newPrice: number | [number, number] | null,
    oldPrice: Maybe<number | [number, number]> = null,
    isBundle: boolean = false,
  ) {
    const colorDefinitions = this._institution.value?.settings.colorDefinitions;
    const inheritedColorDefinitions = this._institution.value?.settings.inheritedColorDefinitions;

    const newColor = getColor(
      colorDefinitions?.registrationColorDefinitions.newPriceColor,
      inheritedColorDefinitions?.value?.registrationColorDefinitions?.newPriceColor,
      this.colors.tone.error,
    );
    const oldColor = getColor(
      colorDefinitions?.registrationColorDefinitions?.oldPriceColor,
      inheritedColorDefinitions?.value?.registrationColorDefinitions?.oldPriceColor,
      '#000',
    );

    if (oldPrice === null)
      return html` <tr-text bold ${css({ fontSize: '24px', color: newColor })}> ${formatPrice(newPrice)} </tr-text> `;

    const oldPriceStr = formatPrice(oldPrice, '0,00 €');
    if (newPrice === null || (!(newPrice instanceof Array) && !isNaN(newPrice) && newPrice < 0))
      return this._renderBundledPrice('0,00 €', newColor, oldPriceStr, oldColor);

    const newPriceStr = formatPrice(newPrice, '0,00 €');

    return this._renderBundledPrice(newPriceStr, newColor, oldPriceStr, oldColor);
  }

  protected _renderBundledPrice(newPriceStr: string, newColor: string, oldPriceStr: string, oldColor: string) {
    return html`
      <tr-flex horizontal space="sm" end crossaxis-center>
        <tr-h4 bold ${css({ textDecoration: 'line-through', color: oldColor })}> ${oldPriceStr} </tr-h4>
        <tr-h3 bold ${css({ color: newColor })} bold align-end> ${newPriceStr} </tr-h3>
      </tr-flex>
    `;
  }

  private chooseValueByLang(values: { [k in Language]?: string }) {
    return values[this._language.value] ?? '<i>there is no description in this language</i>';
  }

  private _timeSlotTime(start: Maybe<DateTime>, end: Maybe<DateTime>) {
    if (!start || !end) return html` <tr-text> --- </tr-text> `;
    return html`
      <tr-flex
        space="xxs"
        ${css({
          background: getColor(
            this._institution.value?.settings.colorDefinitions?.registrationColorDefinitions.productColor,
            this._institution.value?.settings.inheritedColorDefinitions?.value?.registrationColorDefinitions
              ?.productColor,
            this.colors.tone.black,
          ),
          color: '#fff',
          padding: this.spacing.sm,
          borderRadius: this.sizes.borderRadius,
        })}
      >
        <tr-flex space="xxs" crossaxis-center>
          ${(() => {
            const displayDay = (text: string, day: number, month: string | null) => html`
              <tr-h3 bold ${css({ color: 'white' })}>
                <tr-text small ${css({ display: 'inline-block' })}> ${text} </tr-text>
                ${day} ${month}
              </tr-h3>
            `;
            // same day
            if (start.day === end.day)
              return displayDay(this._translation.translate('on'), start.day, start.monthShort);

            // two days
            if (start.day + 1 === end.day)
              return html`
                ${displayDay(this._translation.translate('on'), start.day, start.monthShort)}
                ${displayDay(this._translation.translate('and'), end.day, end.monthShort)}
              `;

            // more then two days
            return html`
              ${displayDay(this._translation.translate('from'), start.day, start.monthShort)}
              ${displayDay(this._translation.translate('till'), end.day, end.monthShort)}
            `;
          })()}
        </tr-flex>
      </tr-flex>
    `;
  }

  override styles() {
    const institution = this._institution.value;
    if (!institution) return [];

    const settings = institution.settings;

    return [
      ...super.styles(),
      {
        'tr-h3': {
          color: this.colors.tone.placeholder.shade(400),
        },
        'x-card': {
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto',
        },
        '.step-card-text': {
          height: '25px',
          color: '#fff',
          borderRadius: this.sizes.borderRadius,
        },
        '.step-card-icon': {
          height: '25px',
          width: '25px',
          color: '#fff',
          borderRadius: this.sizes.borderRadius,
        },
        '.active .step-card-icon, .active .step-card-text': {
          background: getColor(
            settings.colorDefinitions?.registrationColorDefinitions.stepColor,
            settings.inheritedColorDefinitions?.value?.registrationColorDefinitions.stepColor,
            this.colors.accent,
          ),
        },
        '.active tr-text': {
          fontWeight: '600',
        },
        '.inactive .step-card-icon, .inactive .step-card-text': {
          background: '#d8d8d6',
        },

        '[sub-content]': {
          paddingLeft: this.spacing.sm,
          gap: `${this.spacing.sm} ${this.spacing.md}`,

          'tr-text': {
            lineHeight: this.spacing.md,
            paddingTop: '3px',
          },
        },

        '[first-row-col]': {
          borderLeft: `3px solid ${this.colors.dark.shade(200)}`,
          paddingLeft: `CALC( ${this.spacing.xxs} * 1.5 )`,
        },
      },
    ];
  }
}
